/* ==========================================
   HA Random Products (Homepage blocks)
   Picks random products from collections/search
   and displays them in a custom grid.
   ========================================== */
window.__CUSTOM_JS_PARSED__ = true;
console.log("🧾 ha-random.js parsed ✅");

(() => {
  const DEBUG = false;

  // 🎲 Random unique sample from array
  function sampleUnique(arr, n) {
    const copy = arr.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, n);
  }

  function moneyFromCents(cents, currency = "EUR", locale = "nl-NL") {
    if (typeof cents !== "number" || Number.isNaN(cents)) return "";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  async function fetchCollectionProductsJSON(handle, limitFetch = 250) {
    const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${limitFetch}`;
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Collection JSON fetch failed: ${res.status}`);
    return await res.json(); // { products: [...] }
  }

  async function fetchSearchHTML(query) {
    const url = `/search?type=product&options[prefix]=last&q=${encodeURIComponent(query)}&view=`;
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Search fetch failed: ${res.status}`);
    return await res.text();
  }

  function extractProductLinksFromHTML(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const links = Array.from(doc.querySelectorAll('a[href^="/products/"]'))
      .map((a) => a.getAttribute("href"))
      .filter(Boolean)
      .map((href) => href.split("?")[0]);
    return Array.from(new Set(links));
  }

  async function fetchProductCardHTML(productUrl) {
    const res = await fetch(productUrl, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Product fetch failed: ${res.status} ${productUrl}`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, "text/html");

    const title =
      doc.querySelector("h1")?.textContent?.trim() ||
      doc.querySelector('meta[property="og:title"]')?.content?.trim() ||
      "Product";

    const img = doc.querySelector('meta[property="og:image"]')?.content || "";

    const price =
      doc.querySelector('[data-product-price]')?.textContent?.trim() ||
      doc.querySelector(".price")?.textContent?.trim() ||
      "";

    return { url: productUrl, title, img, priceText: price };
  }

  function escapeHTML(s) {
    return (s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  // Build wrapper + apply width/padding controls from data attributes
  function buildWrapper(block) {
    const title = (block.dataset.title || "").trim();
    const subtitle = (block.dataset.subtitle || "").trim();
    const url = (block.dataset.url || "").trim();
    const button = (block.dataset.button || "View all").trim();

    // Layout controls
    const pad = parseInt(block.dataset.pad || "0", 10);        // px
    const max = parseInt(block.dataset.max || "0", 10);        // px
    const padStyle = pad ? `padding-left:${pad}px; padding-right:${pad}px;` : "";
    const maxStyle = max ? `max-width:${max}px;` : "";

    const titleUrl = (block.dataset.titleUrl || "").trim();

    const headerHTML = (title || subtitle)
      ? `
        <div class="ha-head">
          ${title ? (titleUrl
            ? `<h2 class="ha-h2"><a href="${titleUrl}">${escapeHTML(title)}</a></h2>`
            : `<h2 class="ha-h2">${escapeHTML(title)}</h2>`)
          : ""}
          ${subtitle ? `<div class="ha-sub">${escapeHTML(subtitle)}</div>` : ""}
        </div>
      `
      : "";

    // 🔼 View all knop (was "footer", nu boven cards)
    const ctaHTML = url
      ? `
        <div class="ha-foot">
          <a class="ha-viewall" href="${url}">${escapeHTML(button)}</a>
        </div>
      `
      : "";

    // 🔧 Volgorde aangepast: head → CTA → body (cards)
    block.innerHTML = `
      <div class="ha-outer" style="${padStyle}">
        <div class="ha-wrap" style="${maxStyle}">
          ${headerHTML}
          ${ctaHTML}
          <div class="ha-body">
            <div class="ha-loading">🎲 Loading random products…</div>
          </div>
        </div>
      </div>
    `;

    return block.querySelector(".ha-body");
  }

  function renderCards(bodyEl, products) {
    const cards = products
      .map((p) => {
        const safeTitle = escapeHTML(p.title || "");
        const safePrice = escapeHTML(p.price || "");
        const img = p.img ? `<img loading="lazy" src="${p.img}" alt="${safeTitle}">` : "";

        return `
          <div class="ha-card">
            <a class="ha-image-wrap" href="${p.url}">
              ${img}
            </a>
            <div class="ha-footer">
              <div class="ha-title">${safeTitle}</div>
              <div class="ha-price">${safePrice}</div>
            </div>
          </div>
        `;
      })
      .join("");

    bodyEl.innerHTML = `<div class="ha-grid">${cards}</div>`;
  }

  function injectStylesOnce() {
    if (document.getElementById("ha-random-style")) return;

    const style = document.createElement("style");
    style.id = "ha-random-style";
    style.textContent = `
      /* Section-like margins */
      .ha-random-products { margin: 30px 0 50px; }

      /* Outer always full width, inner centered */
      .ha-outer { width: 100%; margin: -20px 0 0 0;}
      .ha-wrap { margin: 0 auto; }

      /* Header */
      .ha-head{ margin: 0 0 18px 36px; }
      .ha-h2{
        margin: 0 0 6px;
        font-size: 26px;
        font-weight: 700;
        letter-spacing: .2px;
      }
      .ha-sub{ font-size: 14px; opacity: .8; margin:  0 0 30px 0; }

      /* Grid */
      .ha-grid{
        display:grid;
        grid-template-columns: repeat(6, minmax(0,1fr));
        gap: 22px;
      }
      @media (max-width: 1200px){
        .ha-grid{ grid-template-columns: repeat(3, minmax(0,1fr)); }
      }
      @media (max-width: 700px){
        .ha-grid{ grid-template-columns: repeat(2, minmax(0,1fr)); }
      }

      .ha-loading { padding: 14px 0; opacity: .8; }

      /* Card */
      .ha-card{
        border-radius: 16px;
        overflow:hidden;
        background:#000;
        box-shadow: 0 6px 18px rgba(0,0,0,.15);
        transition: transform .2s ease;
      }
      .ha-card:hover{ transform: translateY(-4px); }

      /* Image */
      .ha-image-wrap{
        display:block;
        background:#e9e9e9;
      }
      .ha-image-wrap img{
        width:100%;
        height:auto;
        display:block;
      }

      /* Footer bar (in card) */
      .ha-footer{
        background:#000;
        color:#fff;
        text-align:center;
        padding: 32px 10px 34px;
      }
      .ha-title{
        font-size: 13px;
        letter-spacing:.2px;
        margin-bottom: 4px;
        line-height: 1.2;
      }
      .ha-price{
        font-size: 16px;
        opacity:.95;
        line-height: 1.2;
      }

      /* 🔼 View all knop nu boven de cards */
      .ha-foot{
        display:flex;
        justify-content:center;
        margin-top: 0;
        margin-bottom: 24px;
      }
      .ha-viewall{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        padding: 10px 18px;
        border-radius: 6px;
        background:#111;
        color:#fff;
        text-decoration:none;
        font-size: 13px;
        font-weight: 600;
        transition: transform .2s ease, opacity .2s ease;
      }
      .ha-viewall:hover{
        transform: translateY(-2px);
        opacity:.95;
      }
      .ha-h2 a {
        color: inherit;
        text-decoration: none;
      }
      .ha-h2 a:hover {
        opacity: 0.7;
      }       
    `;
    document.head.appendChild(style);
  }

  async function hydrateRandomBlock(block) {
    const limit = parseInt(block.dataset.limit || "6", 10);
    const collection = (block.dataset.collection || "").trim();
    const query = (block.dataset.query || "").trim();

    const bodyEl = buildWrapper(block);

    if (collection) {
      try {
        const data = await fetchCollectionProductsJSON(collection, 250);
        const products = (data.products || []).filter(Boolean);

        if (DEBUG) console.log("Collection products:", collection, products.length);

        if (!products.length) {
          bodyEl.innerHTML = `<div class="ha-loading">⚠️ No products found in collection: <b>${escapeHTML(collection)}</b></div>`;
          return;
        }

        const currency =
          (window.Shopify && Shopify.currency && Shopify.currency.active)
            ? Shopify.currency.active
            : "EUR";

        const picked = sampleUnique(products, Math.min(limit, products.length)).map((p) => {
          const img = p.images?.[0]?.src || "";
          const variant = p.variants?.[0];

          let priceStr = "";
          if (variant && typeof variant.price === "string") {
            const cents = Math.round(parseFloat(variant.price) * 100);
            priceStr = moneyFromCents(cents, currency);
          } else if (variant && typeof variant.price === "number") {
            priceStr = moneyFromCents(variant.price, currency);
          }

          return {
            url: p.handle ? `/products/${p.handle}` : "#",
            title: p.title || "Product",
            img,
            price: priceStr,
          };
        });

        renderCards(bodyEl, picked);
        return;
      } catch (err) {
        console.warn("HA random (collection) failed, falling back to search if available:", err);
      }
    }

    if (query) {
      try {
        const html = await fetchSearchHTML(query);
        const urls = extractProductLinksFromHTML(html);

        if (!urls.length) {
          bodyEl.innerHTML = `<div class="ha-loading">⚠️ No products found for query: <b>${escapeHTML(query)}</b></div>`;
          return;
        }

        const pickedUrls = sampleUnique(urls, Math.min(limit, urls.length));
        const products = [];
        for (const url of pickedUrls) {
          const p = await fetchProductCardHTML(url);
          products.push({
            url: p.url,
            title: p.title,
            img: p.img,
            price: p.priceText || "",
          });
        }

        renderCards(bodyEl, products);
        return;
      } catch (err) {
        console.warn("HA random (search) failed:", err);
      }
    }

    bodyEl.innerHTML = `<div class="ha-loading">⚠️ No data-source provided. Use <code>data-collection</code> or <code>data-query</code>.</div>`;
  }

  async function run() {
    injectStylesOnce();
    const blocks = document.querySelectorAll(".ha-random-products");
    for (const block of blocks) {
      await hydrateRandomBlock(block);
    }
  }

  document.addEventListener("DOMContentLoaded", run);
  document.addEventListener("shopify:section:load", run);
})();